import React from 'react';
import { Box, IconButton, MenuItem, Select, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { DIAGRAM_LAYOUTS, LIMITS, SCENE_ICONS, diagramEdgesFor } from '../sceneTemplates';
import { fieldSx, labelSx, vb } from '../theme';
import { DiagramLayout, SceneType } from '../types';

const selectSx = {
  ...fieldSx['& .MuiOutlinedInput-root'],
  color: vb.text,
  fontFamily: vb.font,
  fontSize: 13,
  background: vb.page,
  '& .MuiOutlinedInput-notchedOutline': { borderColor: vb.border },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: vb.borderStrong },
  '& .MuiSvgIcon-root': { color: vb.textTertiary }
};
const menuProps = {
  PaperProps: { sx: { background: vb.surface, color: vb.text, border: `1px solid ${vb.border}`, maxHeight: 320 } }
};

const Text: React.FC<{
  label: string;
  value: string;
  max: number;
  onChange: (v: string) => void;
  multiline?: boolean;
  optional?: boolean;
  testId?: string;
}> = ({ label, value, max, onChange, multiline, optional, testId }) => (
  <Box>
    <Typography sx={labelSx}>
      {label}
      {optional ? (
        <Box component="span" sx={{ color: vb.textTertiary, fontWeight: 400, ml: 0.5 }}>
          optional
        </Box>
      ) : null}
      <Box
        component="span"
        sx={{ float: 'right', fontWeight: 400, color: value.length > max ? vb.danger : vb.textTertiary }}
      >
        {value.length}/{max}
      </Box>
    </Typography>
    <TextField
      size="small"
      fullWidth
      multiline={multiline}
      minRows={multiline ? 2 : undefined}
      value={value}
      onChange={(e) => onChange(e.target.value.slice(0, max))}
      inputProps={{ 'aria-label': label, maxLength: max, 'data-testid': testId }}
      sx={fieldSx}
    />
  </Box>
);

const List: React.FC<{
  label: string;
  items: string[];
  min: number;
  max: number;
  maxEach: number;
  onChange: (items: string[]) => void;
  numbered?: boolean;
  testId?: string;
}> = ({ label, items, min, max, maxEach, onChange, numbered, testId }) => (
  <Box>
    <Typography sx={labelSx}>
      {label}{' '}
      <Box component="span" sx={{ fontWeight: 400, color: vb.textTertiary }}>
        ({min} to {max})
      </Box>
    </Typography>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      {items.map((it, i) => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Typography
            sx={{ fontFamily: vb.font, fontSize: 12, color: vb.brand, fontWeight: 800, width: 22, textAlign: 'right' }}
          >
            {numbered ? i + 1 : '•'}
          </Typography>
          <TextField
            size="small"
            fullWidth
            value={it}
            onChange={(e) => onChange(items.map((x, j) => (j === i ? e.target.value.slice(0, maxEach) : x)))}
            inputProps={{
              'aria-label': `${label} ${i + 1}`,
              maxLength: maxEach,
              'data-testid': testId ? `${testId}-${i}` : undefined
            }}
            sx={fieldSx}
          />
          <IconButton
            size="small"
            aria-label={`Remove ${label} ${i + 1}`}
            disabled={items.length <= min}
            onClick={() => onChange(items.filter((_, j) => j !== i))}
            sx={{ color: vb.textTertiary }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      ))}
      {items.length < max && (
        <Box
          component="button"
          type="button"
          onClick={() => onChange([...items, ''])}
          sx={{
            alignSelf: 'flex-start',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            background: 'transparent',
            border: `1px dashed ${vb.borderStrong}`,
            color: vb.textSecondary,
            borderRadius: '8px',
            px: 1.25,
            py: 0.5,
            fontFamily: vb.font,
            fontSize: 12,
            cursor: 'pointer'
          }}
        >
          <AddIcon sx={{ fontSize: 14 }} /> Add
        </Box>
      )}
    </Box>
  </Box>
);

// Template-specific inputs (§25–§32). `onChange` receives the whole content.
const SceneFields: React.FC<{
  type: SceneType;
  content: Record<string, any>;
  onChange: (content: Record<string, any>) => void;
  index: number;
}> = ({ type, content, onChange, index }) => {
  const set = (k: string, v: unknown) => onChange({ ...content, [k]: v });
  const tid = (f: string) => `scene-${index}-${f}`;
  switch (type) {
    case 'statement':
      return (
        <Text
          label="Statement"
          value={content.statement || ''}
          max={LIMITS.statement}
          onChange={(v) => set('statement', v)}
          multiline
          testId={tid('statement')}
        />
      );
    case 'key_points':
      return (
        <>
          <Text
            label="Eyebrow"
            value={content.eyebrow || ''}
            max={LIMITS.eyebrow}
            onChange={(v) => set('eyebrow', v || null)}
            optional
            testId={tid('eyebrow')}
          />
          <Text
            label="Title"
            value={content.title || ''}
            max={LIMITS.title}
            onChange={(v) => set('title', v)}
            testId={tid('title')}
          />
          <List
            label="Points"
            items={content.points || []}
            min={LIMITS.keyPoints.min}
            max={LIMITS.keyPoints.max}
            maxEach={LIMITS.point}
            onChange={(v) => set('points', v)}
            testId={tid('point')}
          />
        </>
      );
    case 'two_column':
      return (
        <>
          <Text
            label="Eyebrow"
            value={content.eyebrow || ''}
            max={LIMITS.eyebrow}
            onChange={(v) => set('eyebrow', v || null)}
            optional
            testId={tid('eyebrow')}
          />
          <Text
            label="Title"
            value={content.title || ''}
            max={LIMITS.title}
            onChange={(v) => set('title', v)}
            testId={tid('title')}
          />
          <Text
            label="Body"
            value={content.body || ''}
            max={LIMITS.body}
            onChange={(v) => set('body', v)}
            multiline
            testId={tid('body')}
          />
          <Box>
            <Typography sx={labelSx}>Visual · icon</Typography>
            <Select
              size="small"
              value={content.visual?.name || 'lightbulb'}
              onChange={(e) => set('visual', { type: 'icon', name: e.target.value })}
              sx={selectSx}
              MenuProps={menuProps}
              inputProps={{ 'aria-label': 'Visual icon', 'data-testid': tid('icon') }}
            >
              {SCENE_ICONS.map((n) => (
                <MenuItem key={n} value={n} sx={{ fontFamily: vb.font, fontSize: 13 }}>
                  {n}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </>
      );
    case 'numbered_steps':
      return (
        <>
          <Text
            label="Eyebrow"
            value={content.eyebrow || ''}
            max={LIMITS.eyebrow}
            onChange={(v) => set('eyebrow', v || null)}
            optional
            testId={tid('eyebrow')}
          />
          <Text
            label="Title"
            value={content.title || ''}
            max={LIMITS.title}
            onChange={(v) => set('title', v)}
            testId={tid('title')}
          />
          <List
            label="Steps"
            items={content.steps || []}
            min={LIMITS.steps.min}
            max={LIMITS.steps.max}
            maxEach={LIMITS.step}
            onChange={(v) => set('steps', v)}
            numbered
            testId={tid('step')}
          />
        </>
      );
    case 'scenario_card':
      return (
        <>
          <Text
            label="Label"
            value={content.label || ''}
            max={LIMITS.label}
            onChange={(v) => set('label', v)}
            testId={tid('label')}
          />
          <Text
            label="Title"
            value={content.title || ''}
            max={LIMITS.title}
            onChange={(v) => set('title', v)}
            testId={tid('title')}
          />
          <Text
            label="Scenario"
            value={content.body || ''}
            max={LIMITS.body}
            onChange={(v) => set('body', v)}
            multiline
            testId={tid('body')}
          />
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
            <Text
              label="Persona name"
              value={content.personaName || ''}
              max={LIMITS.persona}
              onChange={(v) => set('personaName', v || null)}
              optional
              testId={tid('personaName')}
            />
            <Text
              label="Persona role"
              value={content.personaRole || ''}
              max={LIMITS.persona}
              onChange={(v) => set('personaRole', v || null)}
              optional
              testId={tid('personaRole')}
            />
          </Box>
        </>
      );
    case 'stat_highlight':
      return (
        <>
          <Text
            label="Value"
            value={content.value || ''}
            max={LIMITS.statValue}
            onChange={(v) => set('value', v)}
            testId={tid('value')}
          />
          <Text
            label="Label"
            value={content.label || ''}
            max={LIMITS.statLabel}
            onChange={(v) => set('label', v)}
            testId={tid('label')}
          />
          <Text
            label="Source"
            value={content.source || ''}
            max={LIMITS.source}
            onChange={(v) => set('source', v || null)}
            optional
            testId={tid('source')}
          />
          <Typography sx={{ fontFamily: vb.font, fontSize: 12, color: vb.textTertiary }}>
            Only use a number that appears in the Lesson Body.
          </Typography>
        </>
      );
    case 'diagram': {
      const nodes: Array<{ id: string; label: string }> = content.nodes || [];
      const setNodes = (next: Array<{ id: string; label: string }>, layout: DiagramLayout = content.layout) =>
        onChange({ ...content, layout, nodes: next, edges: diagramEdgesFor(layout, next) });
      const { min, max } = LIMITS.diagramNodes;
      return (
        <>
          <Text
            label="Eyebrow"
            value={content.eyebrow || ''}
            max={LIMITS.eyebrow}
            onChange={(v) => set('eyebrow', v || null)}
            optional
            testId={tid('eyebrow')}
          />
          <Text
            label="Title"
            value={content.title || ''}
            max={LIMITS.title}
            onChange={(v) => set('title', v)}
            testId={tid('title')}
          />
          <Box>
            <Typography sx={labelSx}>Layout</Typography>
            <Select
              size="small"
              value={content.layout || 'spoke_and_wheel'}
              onChange={(e) => setNodes(nodes, e.target.value as DiagramLayout)}
              sx={selectSx}
              MenuProps={menuProps}
              inputProps={{ 'aria-label': 'Diagram layout', 'data-testid': tid('layout') }}
            >
              {DIAGRAM_LAYOUTS.map((l) => (
                <MenuItem key={l.value} value={l.value} sx={{ fontFamily: vb.font, fontSize: 13 }}>
                  {l.label}
                </MenuItem>
              ))}
            </Select>
          </Box>
          <List
            label={content.layout === 'grid_2x2' ? 'Nodes (exactly 4)' : 'Nodes · first is the hub'}
            items={nodes.map((n) => n.label)}
            min={content.layout === 'grid_2x2' ? 4 : min}
            max={content.layout === 'grid_2x2' ? 4 : max}
            maxEach={LIMITS.nodeLabel}
            onChange={(labels) => setNodes(labels.map((label, i) => ({ id: nodes[i]?.id ?? `n${i + 1}`, label })))}
            numbered
            testId={tid('node')}
          />
        </>
      );
    }
    case 'summary':
      return (
        <>
          <Text
            label="Eyebrow"
            value={content.eyebrow || ''}
            max={LIMITS.eyebrow}
            onChange={(v) => set('eyebrow', v)}
            testId={tid('eyebrow')}
          />
          <Text
            label="Title"
            value={content.title || ''}
            max={LIMITS.title}
            onChange={(v) => set('title', v)}
            testId={tid('title')}
          />
          <List
            label="Takeaways"
            items={content.takeaways || []}
            min={LIMITS.takeaways.min}
            max={LIMITS.takeaways.max}
            maxEach={LIMITS.takeaway}
            onChange={(v) => set('takeaways', v)}
            testId={tid('takeaway')}
          />
        </>
      );
    default:
      return null;
  }
};

export default SceneFields;
